import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import { fetchPromoAssignedUser, fetchPromoHistory } from '../../api/promoCodesApi';
import { getApiErrorText } from '../../utils/apiResponse';
import { formatPrice } from '../../utils/utils';
import { promoDateLabel } from '../../utils/promoDates';
import { getPromoStatus, promoStatuses } from './promoListQuery';

const usageStatuses = { COMMITTED: 'Зараховано', RESERVED: 'Резерв', RELEASED: 'Застосування скасовано', MERGED: 'Об’єднано', ALLOCATED: 'Перенесено зі знижкою' };
const historyPageSize = 50;

export default function AdminPromoDetailsModal({ promo, categories, busy, onClose, onEdit, onToggle }) {
  const [tab, setTab] = useState('details');
  const [rows, setRows] = useState([]);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [actionError, setActionError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [copied, setCopied] = useState('');
  const [owner, setOwner] = useState(null);
  const mounted = useRef(true);
  const changing = useRef(false);
  const codeInput = useRef(null);
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => {
    setOwner(null);
    if (!promo.assignedUserId) return;
    let current = true;
    fetchPromoAssignedUser(promo.id).then(result => {
      if (current) setOwner(result);
    }).catch(() => {});
    return () => { current = false; };
  }, [promo.id, promo.assignedUserId]);
  useEffect(() => {
    if (tab !== 'history') return;
    let current = true;
    setLoading(true); setHistoryError(''); setRows([]);
    fetchPromoHistory(promo.id, offset).then(result => { if (current) setRows(result || []); })
      .catch(reason => { if (current) setHistoryError(getApiErrorText(reason)); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [promo.id, tab, offset, refresh]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(promo.code);
      if (mounted.current) setCopied('Промокод скопійовано');
    } catch (_) {
      if (!mounted.current) return;
      codeInput.current?.focus(); codeInput.current?.select();
      setCopied('Скопіюйте виділений код із поля');
    }
  };
  const toggle = async () => {
    if (busy || changing.current) return;
    changing.current = true; setActionError('');
    try { await onToggle(promo); }
    catch (reason) { if (mounted.current) setActionError(getApiErrorText(reason)); }
    finally { changing.current = false; }
  };
  const status = getPromoStatus(promo);
  const ownerName = [owner?.surname, owner?.name]
    .map(value => String(value || '').trim()).filter(Boolean).join(' ');
  const ownerLabel = [ownerName, owner?.phone]
    .map(value => String(value || '').trim()).filter(Boolean).join(' · ');
  const field = (label, value) => <div><dt>{label}</dt><dd>{value}</dd></div>;
  const categoryLabel = promo.categoryScope === 'ALL' ? 'Усі категорії' : (promo.categoryIds || [])
    .map(id => categories.find(category => category.id === id)?.name?.replace(/<[^>]*>/g, '') || `#${id}`).join(', ');

  return <Modal show onHide={() => { if (!busy) onClose(); }} centered size='lg' animation={false}
    backdrop={busy ? 'static' : true} keyboard={!busy} className='promo-admin-modal promo-details-modal' aria-labelledby='promo-details-title'>
    <Modal.Header>
      <Modal.Title as='h5' id='promo-details-title'>Промокод <span className='text-muted'>#{promo.id}</span></Modal.Title>
      <button type='button' className='close' disabled={busy} aria-label='Закрити' onClick={onClose}><span aria-hidden='true'>×</span></button>
    </Modal.Header>
    <Modal.Body>
      <div className='promo-details-overview'>
        <div className='promo-details-discount'>
          <strong>{formatPrice(promo.discountValue)}<span>{promo.discountType === 'PERCENT' ? '%' : ' грн'}</span></strong>
          <span>знижка</span>
        </div>
        <div className='promo-details-overview-content'>
          <div className='promo-details-overview-meta'>
            <span className={`promo-details-status promo-status-${status}`}><span aria-hidden='true'>●</span>{promoStatuses[status]}</span>
            <span>{promo.campaignSource === 'WHEEL' ? 'Колесо знижок' : promo.campaignSource || 'Звичайний промокод'}</span>
          </div>
          <div className='promo-details-code'>
            <input ref={codeInput} className='form-control' value={promo.code} readOnly aria-label='Код промокоду' onFocus={event => event.target.select()} />
            <button type='button' className='btn btn-outline-secondary' onClick={copy} title='Скопіювати промокод'><i className='bi bi-copy mr-2' aria-hidden='true' />Скопіювати</button>
          </div>
          <div className='promo-details-owner'><i className='bi bi-person mr-1' aria-hidden='true' />
            {promo.assignedUserId ? <Link to={`/admin/users/${promo.assignedUserId}`}>{ownerLabel ? `#${promo.assignedUserId} · ${ownerLabel}` : `Покупець #${promo.assignedUserId}`}</Link> : 'Усі покупці'}
          </div>
        </div>
      </div>
      <p className='promo-details-copy small text-muted' role='status'>{copied}</p>
      <div className='nav nav-tabs promo-details-tabs' role='tablist' aria-label='Інформація про промокод'>
        <button type='button' role='tab' id='promo-details-tab' aria-selected={tab === 'details'} aria-controls='promo-details-panel'
          className={`promo-details-tab${tab === 'details' ? ' is-active' : ''}`} onClick={() => setTab('details')}>Умови</button>
        <button type='button' role='tab' id='promo-history-tab' aria-selected={tab === 'history'} aria-controls='promo-history-panel'
          className={`promo-details-tab${tab === 'history' ? ' is-active' : ''}`} onClick={() => setTab('history')}>Історія</button>
      </div>
      {tab === 'details' ? <div id='promo-details-panel' role='tabpanel' aria-labelledby='promo-details-tab'>
        <div className='promo-details-sections'>
          <section className='promo-details-section'>
            <h6><i className='bi bi-calendar3' aria-hidden='true' />Термін дії</h6>
            <dl className='promo-details-fields'>
              {field('Початок', promo.startsAt ? promoDateLabel(promo.startsAt) : 'Без обмеження')}
              {field('Завершення', promo.expiresAt ? promoDateLabel(promo.expiresAt) : 'Без обмеження')}
            </dl>
          </section>
          <section className='promo-details-section'>
            <h6><i className='bi bi-arrow-repeat' aria-hidden='true' />Використання</h6>
            <dl className='promo-details-fields'>
              {field('Використано', `${promo.usageCount ?? 0} / ${promo.usageLimitTotal ?? 'Без ліміту'}`)}
              {field('На покупця', promo.usageLimitPerUser ?? 'Без ліміту')}
            </dl>
          </section>
          <section className='promo-details-section promo-details-section--wide'>
            <h6><i className='bi bi-sliders' aria-hidden='true' />Умови застосування</h6>
            <dl className='promo-details-fields promo-details-fields--conditions'>
              {field('Мінімальна сума', promo.minOrderAmount ? `${formatPrice(promo.minOrderAmount)} грн` : 'Без мінімуму')}
              {field('Максимальна знижка', promo.maxDiscountAmount != null ? `${formatPrice(promo.maxDiscountAmount)} грн` : 'Без обмеження')}
              {field('Категорії', categoryLabel || 'Не обрано')}
              {promo.categoryScope === 'SELECTED' && field('Підкатегорії', promo.includeSubcategories ? 'Включені' : 'Не включені')}
            </dl>
            <div className='promo-details-rules'>
              <span className={promo.appliesToSaleItems ? 'promo-details-rule--enabled' : ''}><i className={`bi bi-${promo.appliesToSaleItems ? 'check-circle-fill' : 'dash-circle'} mr-2`} aria-hidden='true' />{promo.appliesToSaleItems ? 'Діє на акційні товари' : 'Без акційних товарів'}</span>
              <span className={promo.combinesWithPersonalDiscount ? 'promo-details-rule--enabled' : ''}><i className={`bi bi-${promo.combinesWithPersonalDiscount ? 'check-circle-fill' : 'dash-circle'} mr-2`} aria-hidden='true' />{promo.combinesWithPersonalDiscount ? 'Сумується з персональною знижкою' : 'З персональною знижкою застосовується вигідніша пропозиція'}</span>
            </div>
          </section>
        </div>
        {promo.description && <details className='promo-details-comment'><summary><i className='bi bi-chat-left-text mr-2' aria-hidden='true' />Внутрішній коментар</summary><p>{promo.description}</p></details>}
      </div> : <div id='promo-history-panel' role='tabpanel' aria-labelledby='promo-history-tab'>
        {loading ? <p className='text-muted py-3' role='status'>Завантажуємо історію…</p> : historyError ? <div className='alert alert-danger mt-3' role='alert'>
          {historyError}<button type='button' className='btn btn-link btn-sm' onClick={() => setRefresh(value => value + 1)}>Повторити</button>
        </div> : rows.length ? <div className='table-responsive'><table className='table table-sm promo-history-table'>
          <thead><tr><th>Замовлення</th><th>Покупець</th><th>Знижка</th><th>Стан</th><th>Дата (Київ)</th></tr></thead>
          <tbody>{rows.map(row => <tr key={row.id}>
            <td>{(row.orderId || row.originalOrderId) ? <Link to={`/admin/order/${row.orderId || row.originalOrderId}`}>#{row.orderId || row.originalOrderId}</Link> : `#${row.originalOrderId} (архів)`}
              {row.mergedIntoOrderId && <div>→ <Link to={`/admin/order/${row.mergedIntoOrderId}`}>#{row.mergedIntoOrderId}</Link></div>}</td>
            <td>{row.userId ? <Link to={`/admin/users/${row.userId}`}>#{row.userId}</Link> : 'Гість'}</td>
            <td>{formatPrice(row.discountAmount)} грн</td><td>{usageStatuses[row.status] || row.status}</td><td>{promoDateLabel(row.createdAt)}</td>
          </tr>)}</tbody>
        </table></div> : <p className='text-muted py-3 mb-0'>Використань ще немає</p>}
        <div className='d-flex justify-content-between align-items-center pt-3'>
          <button type='button' className='btn btn-sm btn-outline-secondary' disabled={!offset || loading} onClick={() => setOffset(value => Math.max(0, value - historyPageSize))}>Попередні</button>
          <span className='small text-muted'>Сторінка {offset / historyPageSize + 1}</span>
          <button type='button' className='btn btn-sm btn-outline-secondary' disabled={loading || Boolean(historyError) || rows.length < historyPageSize} onClick={() => setOffset(value => value + historyPageSize)}>Наступні</button>
        </div>
      </div>}
      {actionError && <div className='alert alert-danger mt-3 mb-0' role='alert'>{actionError}</div>}
    </Modal.Body>
    <Modal.Footer>
      <button type='button' className='btn btn-secondary' disabled={busy} onClick={onClose}>Закрити</button>
      <button type='button' className='btn btn-outline-secondary' disabled={busy} onClick={toggle}>{busy ? 'Зберігаємо…' : promo.isActive ? 'Вимкнути' : 'Увімкнути'}</button>
      {promo.campaignSource !== 'WHEEL' && <button type='button' className='btn btn-dark' disabled={busy} onClick={() => onEdit(promo)}>Редагувати</button>}
    </Modal.Footer>
  </Modal>;
}
